import React, { useEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
    value: string;
    label: string;
}

function filterOptions(options: MultiSelectOption[], search: string): MultiSelectOption[] {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(lower));
}

interface TextPlaceholderOpts { disabled: boolean; loading: boolean; selectedCount: number; placeholder: string; disabledMessage: string; }
function getTextPlaceholder({ disabled, loading, selectedCount, placeholder, disabledMessage }: TextPlaceholderOpts): string {
    if (disabled) return disabledMessage;
    if (loading) return 'Loading...';
    return selectedCount === 0 ? placeholder : '';
}

interface MultiSelectListProps {
    options: MultiSelectOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    disabled?: boolean;
    loading?: boolean;
    invalid?: boolean;
    placeholder?: string;
    disabledMessage?: string;
    emptyMessage?: string;
}

const MultiSelectList: React.FC<MultiSelectListProps> = ({
    options,
    selectedValues,
    onChange,
    disabled = false,
    loading = false,
    invalid = false,
    placeholder = 'Select...',
    disabledMessage = 'Select a customer account first',
    emptyMessage = 'No options available',
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const filtered = filterOptions(options, search);
    const allSelected = options.length > 0 && options.every((o) => selectedValues.includes(o.value));

    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, []);

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    const toggleOne = (val: string) => {
        const updated = selectedValues.includes(val)
            ? selectedValues.filter((v) => v !== val)
            : [...selectedValues, val];
        onChange(updated);
    };

    const toggleAll = () => {
        onChange(allSelected ? [] : options.map((o) => o.value));
    };

    const handleWrapClick = () => {
        if (!disabled && !loading) {
            setOpen(true);
            inputRef.current?.focus();
        }
    };

    const textPlaceholder = getTextPlaceholder({ disabled, loading, selectedCount: selectedValues.length, placeholder, disabledMessage });

    return (
        <div
            ref={containerRef}
            className={`multi-select-dropdown${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`}
        >
            <div
                className={`multi-select-dropdown__input-wrap${invalid ? ' is-invalid' : ''}`}
                onClick={handleWrapClick}
            >
                {selectedValues.map((val) => {
                    const opt = options.find((o) => o.value === val);
                    if (!opt) return null;
                    return (
                        <span key={val} className="multi-select-dropdown__tag">
                            <span className="multi-select-dropdown__tag-label">{opt.label}</span>
                            <button
                                type="button"
                                className="multi-select-dropdown__tag-remove"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); toggleOne(val); }}
                                aria-label={`Remove ${opt.label}`}
                                tabIndex={-1}
                            >
                                &times;
                            </button>
                        </span>
                    );
                })}

                <input
                    ref={inputRef}
                    type="text"
                    className="multi-select-dropdown__text-input"
                    placeholder={textPlaceholder}
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
                    onFocus={() => { if (!disabled && !loading) setOpen(true); }}
                    disabled={disabled || loading}
                />

                {selectedValues.length > 0 && !disabled && !loading && (
                    <button
                        type="button"
                        className="multi-select-dropdown__clear-all"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onChange([]); setSearch(''); }}
                        aria-label="Clear all"
                        tabIndex={-1}
                    >
                        &times;
                    </button>
                )}
            </div>

            {loading && <span className="multi-select-dropdown__spinner" aria-hidden="true" />}

            {open && !disabled && !loading && (
                <div className="multi-select-dropdown__panel" onMouseDown={(e) => e.stopPropagation()}>
                    {options.length === 0 ? (
                        <p className="multi-select-dropdown__empty">{emptyMessage}</p>
                    ) : filtered.length === 0 ? (
                        <p className="multi-select-dropdown__empty">No option</p>
                    ) : (
                        <>
                            <label className={`multi-select-dropdown__item multi-select-dropdown__item--all${allSelected ? ' is-checked' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleAll}
                                />
                                <span>Select all</span>
                            </label>
                            {filtered.map((o) => (
                                <label
                                    key={o.value}
                                    className={`multi-select-dropdown__item${selectedValues.includes(o.value) ? ' is-checked' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(o.value)}
                                        onChange={() => toggleOne(o.value)}
                                    />
                                    <span>{o.label}</span>
                                </label>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiSelectList;
